import { useEffect, useRef, useState } from 'react';
import BookingCalendar from './BookingCalendar';
import {
  BuildingIcon,
  ClockIcon,
  EmailIcon,
  LocationIcon,
  PhoneIcon,
  PostcodeIcon,
  ServiceIcon,
  UserIcon,
} from './Icons';
import { getBookingAvailability, submitBooking } from '../services/api';

const TIME_SLOTS = [
  { value: '11:30', label: '11:30 AM' },
  { value: '14:30', label: '02:30 PM' },
  { value: '16:00', label: '04:00 PM' },
];

const THURSDAY = 4; // Date#getDay(): Sunday=0 .. Saturday=6

// 11:30 isn't offered on Thursdays. Must match _BLOCKED_SLOTS_BY_WEEKDAY in
// backend/app/services/booking_service.py — the backend is the real guard,
// this just keeps the dropdown from offering a slot the server will reject.
const BLOCKED_SLOTS_BY_WEEKDAY = { [THURSDAY]: ['11:30'] };

function blockedSlotsForDate(dateString) {
  if (!dateString) return [];
  const day = new Date(`${dateString}T00:00:00`).getDay();
  return BLOCKED_SLOTS_BY_WEEKDAY[day] || [];
}

const FIELD_DEFINITIONS = [
  {
    id: 'name',
    name: 'name',
    label: 'Your Name',
    type: 'text',
    required: true,
    row: 1,
    icon: UserIcon,
    autoComplete: 'name',
    errorMessage: 'Please enter your full name',
  },
  {
    id: 'email',
    name: 'email',
    label: 'Email Address',
    type: 'email',
    required: true,
    row: 1,
    icon: EmailIcon,
    autoComplete: 'email',
    errorMessage: 'Please enter a valid email address',
  },
  {
    id: 'phone',
    name: 'phone',
    label: 'Phone Number',
    type: 'tel',
    required: true,
    row: 2,
    icon: PhoneIcon,
    autoComplete: 'tel',
    errorMessage: 'Please enter a valid phone number',
  },
  {
    id: 'service',
    name: 'service',
    label: 'Service Required',
    type: 'select',
    required: true,
    row: 2,
    icon: ServiceIcon,
    errorMessage: 'Please select a service',
    options: [
      { value: '', label: 'Select a service' },
      { value: 'driveway', label: 'Driveway' },
      { value: 'paving & walkways', label: 'Paving & Walkways' },
      { value: 'patio', label: 'Patio' },
      { value: 'decking', label: 'Decking' },
      { value: 'fencing', label: 'Fencing' },
      { value: 'gates', label: 'Gates' },
      { value: 'wall', label: 'Wall' },
      { value: 'stairs', label: 'Stairs' },
      { value: 'multiple', label: 'Multiple Services' },
    ],
  },
  {
    id: 'address_line1',
    name: 'address_line1',
    label: 'Address Line 1',
    type: 'text',
    required: true,
    row: 3,
    icon: LocationIcon,
    autoComplete: 'address-line1',
    errorMessage: 'Please enter your address',
  },
  {
    id: 'city',
    name: 'city',
    label: 'City / Town',
    type: 'text',
    required: true,
    row: 4,
    icon: BuildingIcon,
    autoComplete: 'address-level2',
    errorMessage: 'Please enter your city or town',
  },
  {
    id: 'postcode',
    name: 'postcode',
    label: 'Postcode',
    type: 'text',
    required: true,
    row: 4,
    icon: PostcodeIcon,
    autoComplete: 'postal-code',
    uppercase: true,
    placeholder: 'e.g. SW1A 1AA',
    errorMessage: 'Please enter a valid UK postcode',
  },
  {
    id: 'scheduled_date',
    name: 'scheduled_date',
    label: 'Preferred Date',
    type: 'calendar',
    row: 5,
  },
  {
    id: 'scheduled_time',
    name: 'scheduled_time',
    label: 'Preferred Time',
    type: 'select',
    row: 5,
    icon: ClockIcon,
    options: [{ value: '', label: 'Select a time' }, ...TIME_SLOTS],
  },
  {
    id: 'notes',
    name: 'notes',
    label: 'Project Details',
    type: 'textarea',
    placeholder: 'Tell us about your project...',
    row: 6,
  },
];

// Mirrors backend/app/models/validators.py's clean_phone exactly, so a
// number that looks valid here never gets rejected by the server after
// submit — a UK number (no "+") must be 10-11 digits starting with 0;
// an international number (leading "+") must have 8-15 digits.
const isValidPhone = (value) => {
  const isInternational = value.trim().startsWith('+');
  const digits = value.replace(/\D/g, '');
  if (isInternational) {
    return digits.length >= 8 && digits.length <= 15;
  }
  return digits.length >= 10 && digits.length <= 11 && digits.startsWith('0');
};

// Mirrors backend/app/models/validators.py's clean_postcode regex exactly
// (the GOV.UK-published UK postcode pattern, including the GIR 0AA special
// case), so a postcode that looks valid here never gets rejected by the
// server after submit.
const UK_POSTCODE_RE =
  /^(GIR0AA)$|^([A-Z][0-9]{1,2}|[A-Z][A-HJ-Y][0-9]{1,2}|[A-Z][0-9][A-Z]|[A-Z][A-HJ-Y][0-9][A-Z]?)([0-9][A-Z]{2})$/;
const isValidPostcode = (value) =>
  UK_POSTCODE_RE.test(value.replace(/\s/g, '').toUpperCase());

const VALIDATORS = {
  email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
  phone: isValidPhone,
  postcode: isValidPostcode,
};

const EMPTY_FORM = FIELD_DEFINITIONS.reduce(
  (state, field) => ({ ...state, [field.name]: '' }),
  {}
);

const FormField = ({
  label,
  type = 'text',
  id,
  name,
  required,
  errorMessage,
  isValid,
  value,
  onChange,
  onBlur,
  refCallback,
  placeholder,
  options,
  icon: Icon,
  autoComplete,
  autoFocus,
  uppercase,
  unavailableDates,
}) => {
  const errorId = errorMessage ? `${id}-error` : undefined;
  const statusClass = errorMessage ? 'is-error' : isValid ? 'is-valid' : '';
  const uppercaseClass = uppercase ? 'is-uppercase' : '';
  return (
    <div className="form__group">
      <label className="form__label" htmlFor={id}>
        {label}
        {required && <span aria-label="required"> *</span>}
      </label>

      <div className="form__input-wrapper">
        {Icon && <Icon className="form__input-icon" size={18} />}

        {type === 'calendar' ? (
          <BookingCalendar
            id={id}
            selected={value}
            unavailableDates={unavailableDates}
            onSelect={(dateString) =>
              onChange({ target: { name, value: dateString } })
            }
            errorId={errorId}
            isInvalid={Boolean(errorMessage)}
          />
        ) : type === 'textarea' ? (
          <textarea
            id={id}
            name={name}
            className={`form__input ${statusClass}`}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            ref={refCallback}
            rows="4"
            placeholder={placeholder}
            autoComplete={autoComplete}
            aria-describedby={errorId}
            aria-invalid={errorMessage ? true : undefined}
          />
        ) : type === 'select' ? (
          <select
            id={id}
            name={name}
            className={`form__input ${statusClass}`}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            ref={refCallback}
            required={required}
            autoComplete={autoComplete}
            aria-describedby={errorId}
            aria-invalid={errorMessage ? true : undefined}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            id={id}
            name={name}
            className={`form__input ${statusClass} ${uppercaseClass}`}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            ref={refCallback}
            required={required}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            aria-describedby={errorId}
            aria-invalid={errorMessage ? true : undefined}
          />
        )}
      </div>

      {errorMessage && (
        <span id={errorId} className="form__error" role="alert">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

const BookingForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState(new Set());
  const [bookedTimesByDate, setBookedTimesByDate] = useState({});

  const inputRefs = useRef({});

  useEffect(() => {
    let cancelled = false;

    getBookingAvailability(90)
      .then((data) => {
        if (cancelled) return;
        const days = data.days || [];
        setUnavailableDates(
          new Set(days.filter((d) => !d.is_available).map((d) => d.date))
        );
        setBookedTimesByDate(
          Object.fromEntries(days.map((d) => [d.date, d.booked_times || []]))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setUnavailableDates(new Set());
          setBookedTimesByDate({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const unavailableTimesForSelectedDate = [
    ...(bookedTimesByDate[formData.scheduled_date] || []),
    ...blockedSlotsForDate(formData.scheduled_date),
  ];

  useEffect(() => {
    setFormData((prev) => {
      const unavailableTimes = [
        ...(bookedTimesByDate[prev.scheduled_date] || []),
        ...blockedSlotsForDate(prev.scheduled_date),
      ];
      if (
        prev.scheduled_time &&
        unavailableTimes.includes(prev.scheduled_time)
      ) {
        return { ...prev, scheduled_time: '' };
      }
      return prev;
    });
  }, [formData.scheduled_date, bookedTimesByDate]);

  const validateField = (name, value) => {
    const field = FIELD_DEFINITIONS.find((item) => item.name === name);
    const isEmpty = !value.trim();

    if (field?.required && isEmpty) return false;
    if (VALIDATORS[name] && !isEmpty) return VALIDATORS[name](value);
    return true;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const field = FIELD_DEFINITIONS.find((f) => f.name === name);
    const nextValue = field?.uppercase ? value.toUpperCase() : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (apiError) setApiError(null);
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const field = FIELD_DEFINITIONS.find((f) => f.name === name);
    const isValid = validateField(name, value);
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: isValid ? null : field.errorMessage,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};
    let firstInvalidRef = null;

    FIELD_DEFINITIONS.forEach((field) => {
      const isValid = validateField(field.name, formData[field.name]);
      if (!isValid) {
        nextErrors[field.name] = field.errorMessage;
        if (!firstInvalidRef) firstInvalidRef = inputRefs.current[field.name];
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      firstInvalidRef?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalidRef?.focus();
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    const payload = {
      ...formData,
      scheduled_date: formData.scheduled_date || null,
      scheduled_time: formData.scheduled_time || null,
      notes: formData.notes || null,
    };

    try {
      const result = await submitBooking(payload);
      onSuccess({
        bookingNumber: result.booking_number,
        scheduledDate: payload.scheduled_date,
        scheduledTime: payload.scheduled_time,
        service: payload.service,
      });
      setFormData(EMPTY_FORM);
      setErrors({});
      setTouched({});
    } catch (error) {
      setApiError(
        error.message || 'Failed to submit booking. Please try again.'
      );
      console.error('Booking submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const rows = [...new Set(FIELD_DEFINITIONS.map((f) => f.row))];

  return (
    <form
      className="quote-form"
      id="bookingForm"
      onSubmit={handleSubmit}
      noValidate
    >
      {apiError && (
        <div className="form__error-message" role="alert">
          {apiError}
        </div>
      )}

      {rows.map((row) => {
        const rowFields = FIELD_DEFINITIONS.filter((f) => f.row === row);
        const isMultiCol = rowFields.length > 1;
        const fields = rowFields.map((field) => {
          const options =
            field.name === 'scheduled_time'
              ? field.options.filter(
                  (option) =>
                    !option.value ||
                    !unavailableTimesForSelectedDate.includes(option.value)
                )
              : field.options;

          return (
            <FormField
              key={field.name}
              {...field}
              options={options}
              value={formData[field.name]}
              errorMessage={errors[field.name] || null}
              isValid={
                field.required &&
                touched[field.name] &&
                !errors[field.name] &&
                Boolean(formData[field.name].trim())
              }
              onChange={handleChange}
              onBlur={handleBlur}
              refCallback={(el) => {
                inputRefs.current[field.name] = el;
              }}
              unavailableDates={unavailableDates}
            />
          );
        });

        return isMultiCol ? (
          <div key={row} className="form__row">
            {fields}
          </div>
        ) : (
          fields
        );
      })}

      <button
        type="submit"
        className="btn-primary form__submit"
        disabled={isSubmitting}
      >
        {isSubmitting && (
          <span className="form__submit-spinner" aria-hidden="true" />
        )}
        {isSubmitting ? 'Sending…' : 'Book Now'}
      </button>
    </form>
  );
};

export default BookingForm;
